import * as THREE from 'three';
import { BLACK, BONE, DSTEEL, DWOOD, WOOD, basic, cloth, emis, smoke, std } from '../materials.ts';
import { G, M, Ribbon, V, eyesGlow, glowSprite, limb, ring, wisp } from '../prims.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/*
 * Era II nightmares: the sapper that digs under walls, the bone ram that batters gates, the bone mangonel
 * that throws at the walls from afar and the spectral archer that shoots towers. Opaque shared materials
 * wherever possible (the baker folds them into a few draws); one translucent and one self-lit material per
 * model at most, plus cheap glow sprites.
 */

const UP = V(0, 1, 0);

/** Points a unit-height cylinder from a to b (for parts re-aimed every tick, like a bowstring). */
const aim = (m: THREE.Mesh, a: THREE.Vector3, b: THREE.Vector3): void => {
  const v = new THREE.Vector3().subVectors(b, a);
  m.position.copy(a).addScaledVector(v, 0.5);
  m.scale.set(1, v.length(), 1);
  m.quaternion.setFromUnitVectors(UP, v.normalize());
};

/* ---------- sapper (Zapador) ---------- */

/**
 * Small dirty miner creature: hunched, big hood thrust forward, long clawed arms on a two-handed pick.
 * Bones: body (at the hips) carries the torso, the head, both arms with the pick (the pick pivots in the
 * fists), the powder sack and the lantern; the legs bend at the knee.
 */
export const sapper = (o?: BaseOpts): Model => {
  const c = begin(o, 0.62, 38);
  const { g, R, rng } = c;
  const skin = std('#2c271f', { roughness: 0.85, bumpScale: 0.04 });
  const dirt = cloth('#4f3e2c', { bumpScale: 0.05 });
  const hoodM = cloth('#5e4832');
  const sackM = cloth('#3a332b');
  const bodyJ = joint(g, V(0, 0.44, 0), 'body');
  const bd = bodyJ.abs;
  const headJ = joint(bd, V(0, 0.8, 0.22), 'head');
  const armsJ = joint(bd, V(0, 0.74, 0.12), 'arms');
  const sackJ = joint(bd, V(-0.09, 0.86, -0.13), 'sack');
  const lanJ = joint(bd, V(-0.22, 0.5, 0.14), 'lantern');
  const legJ = [-1, 1].map((k) => joint(g, V(k * 0.12, 0.42, -0.02), k < 0 ? 'legL' : 'legR'));
  const shinJ = [-1, 1].map((k, i) => joint(legJ[i]!.abs, V(k * 0.16, 0.25, 0.08), k < 0 ? 'shinL' : 'shinR'));
  // Short sturdy legs, knees bent, on broad clawed feet.
  for (const k of [-1, 1]) {
    const thigh = legJ[k < 0 ? 0 : 1]!.abs, shin = shinJ[k < 0 ? 0 : 1]!.abs;
    const hip = V(k * 0.12, 0.42, -0.02), knee = V(k * 0.16, 0.25, 0.08), ank = V(k * 0.15, 0.09, 0.0);
    limb(thigh, hip, knee, 0.075, 0.065, dirt, 7);
    limb(shin, knee, ank, 0.06, 0.05, dirt, 7);
    M(new THREE.SphereGeometry(0.065, 7, 6), dirt, shin).position.copy(knee);
    const foot = M(new THREE.BoxGeometry(0.13, 0.07, 0.22), skin, shin);
    foot.position.set(k * 0.15, 0.035, 0.05);
    for (let f = 0; f < 3; f++) {
      const cl = M(new THREE.ConeGeometry(0.014, 0.06, 4), BONE, shin);
      cl.position.set(k * 0.15 + (f - 1) * 0.035, 0.03, 0.18);
      cl.rotation.x = Math.PI / 2;
    }
  }
  // Pot-bellied torso leaning forward, belt at the waist.
  const torso = M(new THREE.SphereGeometry(0.2, 12, 9), skin, bd);
  torso.scale.set(1.2, 1.05, 0.95);
  torso.position.set(0, 0.6, 0.05);
  torso.rotation.x = 0.45;
  const belt = M(new THREE.TorusGeometry(0.19, 0.024, 5, 16), DWOOD, bd);
  belt.scale.set(1.12, 0.9, 1);
  belt.position.set(0, 0.47, 0.06);
  belt.rotation.x = Math.PI / 2 + 0.2;
  M(new THREE.BoxGeometry(0.06, 0.05, 0.02), DSTEEL, bd).position.set(0, 0.46, 0.24);
  // Broad shoulders under a hooded mantle; the head thrust forward.
  for (const k of [-1, 1]) M(new THREE.SphereGeometry(0.09, 8, 7), hoodM, bd).position.set(k * 0.21, 0.74, 0.12);
  const mantle = M(new THREE.ConeGeometry(0.29, 0.2, 12, 1, true), hoodM, bd);
  mantle.position.set(0, 0.78, 0.13);
  mantle.rotation.x = 0.35;
  const head = G(headJ.abs, 0, 0.83, 0.3);
  head.rotation.x = 0.15;
  M(new THREE.SphereGeometry(0.13, 10, 8), skin, head).scale.set(1, 0.9, 1.1);
  const hood = M(new THREE.SphereGeometry(0.175, 12, 8, Math.PI / 2 + 0.95, Math.PI * 2 - 1.9, 0, Math.PI / 1.35), hoodM, head);
  hood.position.set(0, 0.03, -0.03);
  hood.rotation.x = -0.3;
  const peak = M(new THREE.ConeGeometry(0.1, 0.28, 8), hoodM, head);
  peak.position.set(0, 0.12, -0.18);
  peak.rotation.x = -1.95;
  // Goblin ears poking out of the hood, a long nose, eyes like coals.
  for (const k of [-1, 1]) {
    const ear = M(new THREE.ConeGeometry(0.04, 0.2, 5), skin, head);
    ear.position.set(k * 0.19, 0.02, 0.0);
    ear.rotation.z = -k * 1.35;
  }
  const nose = M(new THREE.ConeGeometry(0.03, 0.13, 5), skin, head);
  nose.position.set(0, -0.03, 0.18);
  nose.rotation.x = Math.PI / 2 + 0.3;
  M(new THREE.SphereGeometry(0.085, 8, 6), BLACK, head).position.set(0, 0.01, 0.07);
  eyesGlow(head, '#ffb04a', 0.02, 0.135, 0.045, 0.02);
  // Long arms and the pick, swung from the shoulders (the arms bone), the pick pivoting in the fists.
  const piv = G(armsJ.abs, 0, 0.74, 0.12);
  piv.rotation.x = -0.1;
  for (const k of [-1, 1]) {
    const sh = V(k * 0.21, 0, 0), el = V(k * 0.24, -0.22, 0.16), hand = V(k * 0.05, -0.24, 0.32);
    limb(piv, sh, el, 0.055, 0.045, skin, 6);
    limb(piv, el, hand, 0.045, 0.04, skin, 6);
    M(new THREE.SphereGeometry(0.05, 7, 6), skin, piv).position.copy(hand);
    for (let f = 0; f < 3; f++) {
      const cl = M(new THREE.ConeGeometry(0.013, 0.08, 4), BONE, piv);
      cl.position.set(hand.x + (f - 1) * 0.022, hand.y - 0.05, hand.z + 0.04);
      cl.rotation.x = Math.PI * 0.8;
    }
  }
  // Two-handed pick: the haft runs from the fists up over the right shoulder.
  const pickJ = joint(piv, V(0.028, -0.25, 0.31), 'pick');
  const a = V(0, -0.36, 0.38), b = V(0.2, 0.42, -0.12);
  const dir = new THREE.Vector3().subVectors(b, a);
  const L = dir.length();
  const pick = G(pickJ.abs, a.x, a.y, a.z);
  pick.quaternion.setFromUnitVectors(UP, dir.normalize());
  const haft = M(new THREE.CylinderGeometry(0.022, 0.026, L, 6), DWOOD, pick);
  haft.position.y = L / 2;
  const ph = G(pick, 0, L, 0);
  M(new THREE.BoxGeometry(0.07, 0.08, 0.09), DSTEEL, ph);
  const tine = M(new THREE.ConeGeometry(0.032, 0.36, 5), DSTEEL, ph);
  tine.position.set(0, -0.04, 0.17);
  tine.rotation.x = Math.PI / 2 + 0.3;
  const back = M(new THREE.ConeGeometry(0.03, 0.24, 5), DSTEEL, ph);
  back.position.set(0, -0.03, -0.12);
  back.rotation.x = -Math.PI / 2 - 0.3;
  // A small sack of black powder on the back, tied at the neck, its fuse smouldering; a rope across the chest.
  const sk = sackJ.abs;
  const sack = M(new THREE.SphereGeometry(0.115, 10, 8), sackM, sk);
  sack.scale.set(1, 1.1, 0.85);
  sack.position.set(-0.08, 0.74, -0.17);
  sack.rotation.z = 0.25;
  const neck = M(new THREE.ConeGeometry(0.045, 0.1, 8), sackM, sk);
  neck.position.set(-0.11, 0.87, -0.18);
  neck.rotation.z = 0.3;
  ring(sk, 0.03, 0.009, WOOD, 0, 4, 10).position.set(-0.105, 0.85, -0.18);
  limb(sk, V(-0.12, 0.92, -0.18), V(-0.17, 1.0, -0.15), 0.006, 0.006, DWOOD, 4);
  const spark = glowSprite(sk, '#ffb347', 0.15, V(-0.17, 1.01, -0.15), 0.9, true);
  limb(bd, V(-0.18, 0.78, -0.08), V(0.19, 0.55, 0.2), 0.015, 0.015, WOOD, 4);
  // Clods of earth caked on the shoulders and knees.
  for (let i = 0; i < 4; i++) {
    const cl = M(new THREE.DodecahedronGeometry(0.035 + rng() * 0.02, 0), dirt, i < 2 ? bd : shinJ[i % 2 ? 1 : 0]!.abs);
    cl.position.set((i % 2 ? 1 : -1) * (0.17 + rng() * 0.06), i < 2 ? 0.82 : 0.3, 0.1 + R() * 0.1);
    cl.scale.y = 0.6;
    cl.rotation.set(R() * 3, R() * 3, R() * 3);
  }
  // Miner's lantern hooked at the belt, on the left hip (it swings from the hook).
  const lan = G(lanJ.abs, -0.22, 0.47, 0.14);
  limb(lan, V(0, 0.03, 0), V(0, -0.02, 0), 0.006, 0.006, DSTEEL, 4);
  M(new THREE.BoxGeometry(0.07, 0.09, 0.07), emis('#ffe0a0', '#ff9a2a', 2.2), lan).position.y = -0.07;
  M(new THREE.ConeGeometry(0.06, 0.05, 4), DSTEEL, lan).position.y = 0.0;
  M(new THREE.BoxGeometry(0.08, 0.015, 0.08), DSTEEL, lan).position.y = -0.12;
  const glow = glowSprite(lan, '#ff9a2a', 0.5, V(0, -0.07, 0), 0.8, true);
  // Shadow seeping out of it: it is still a nightmare.
  const ribs: Ribbon[] = [];
  const wm = basic('#2b2244', 0.4);
  for (let i = 0; i < 3; i++) {
    const t = i * 2.1;
    ribs.push(new Ribbon(bd, wisp(Math.cos(t) * 0.16, Math.sin(t) * 0.1, 0.6, 6).map((p) => p.add(V(0, 0.55, 0))), 0.022, wm, rng, 0.05));
  }
  // Only the boil here: the swing, the lantern and the bob are the clips' (anim/creatures/siege.ts).
  const t = (): void => {
    for (const r of ribs) r.step();
    glow.material.opacity = 0.6 + rng() * 0.35;
    spark.material.opacity = 0.5 + rng() * 0.5;
  };
  t();
  return done(c, 1.1, t, undefined, bonesOf(c, {
    body: bodyJ.p, head: headJ.p, arms: armsJ.p, pick: pickJ.p, sack: sackJ.p, lantern: lanJ.p,
    legL: legJ[0]!.p, shinL: shinJ[0]!.p, legR: legJ[1]!.p, shinR: shinJ[1]!.p,
  }));
};

/* ---------- ram (Ariete de hueso) ---------- */

const DISC = std('#8f8470', { roughness: 0.9, bumpScale: 0.05 });

/** A bone wheel: a vertebra hub, spokes of rib and a rim of fused bone. Axis along X. */
const boneWheel = (parent: THREE.Object3D, x: number, y: number, z: number, r: number): THREE.Group => {
  const w = G(parent, x, y, z);
  const rim = M(new THREE.TorusGeometry(r, r * 0.18, 5, 14), BONE, w);
  rim.rotation.y = Math.PI / 2;
  const disc = M(new THREE.CylinderGeometry(r * 0.95, r * 0.95, r * 0.22, 14), DISC, w);
  disc.rotation.z = Math.PI / 2;
  const hub = M(new THREE.CylinderGeometry(r * 0.3, r * 0.36, r * 0.6, 8), BONE, w);
  hub.rotation.z = Math.PI / 2;
  // Foramina punched through the vertebra disc.
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2 + 0.4;
    const h = M(new THREE.CylinderGeometry(r * 0.13, r * 0.13, r * 0.24, 6), BLACK, w);
    h.rotation.z = Math.PI / 2;
    h.position.set(0, Math.sin(a) * r * 0.6, Math.cos(a) * r * 0.6);
  }
  // Knobs of vertebra on the rim: they bite the ground.
  for (let i = 0; i < 7; i++) {
    const a = (i / 7) * Math.PI * 2;
    const kn = M(new THREE.ConeGeometry(r * 0.08, r * 0.2, 4), BONE, w);
    kn.position.set(0, Math.sin(a) * r * 1.12, Math.cos(a) * r * 1.12);
    kn.rotation.x = a - Math.PI / 2;
  }
  return w;
};

/**
 * A titan's femur slung under arches of rib on bone wheels, its head a horned skull. Big and slow.
 * Bones: the frame (carriage, arches, hide) rocks on four free wheels; the femur hangs from two chains
 * (a parallelogram: the clips swing both chains and slide the femur along their arc), its jaw hinged;
 * the hide's flaps hang on each side.
 */
export const ram = (o?: BaseOpts): Model => {
  const c = begin(o, 0.9, 39);
  const { g, R, rng } = c;
  const S = 1.3;
  const body = G(g);
  body.scale.setScalar(S);
  const old = std('#a99d84', { roughness: 0.9, bumpScale: 0.04 });
  const hide = std('#2a221e', { roughness: 0.95, side: THREE.DoubleSide, bumpScale: 0.03 });
  const frameJ = joint(body, V(0, 0.3, 0), 'frame');
  const fr = frameJ.abs;
  // Carriage: two long bones as rails, crossed by shorter ones.
  for (const k of [-1, 1]) {
    limb(fr, V(k * 0.36, 0.36, -0.75), V(k * 0.36, 0.36, 0.6), 0.05, 0.045, old, 7);
    for (const z of [-0.75, 0.6]) M(new THREE.SphereGeometry(0.07, 7, 6), old, fr).position.set(k * 0.36, 0.36, z);
  }
  for (const z of [-0.55, 0.4]) limb(fr, V(-0.48, 0.3, z), V(0.48, 0.3, z), 0.035, 0.035, old, 6);
  // Wheels on their own pivots (not the frame's): they stay on the ground while the frame rocks.
  const WHEELS = [['wheelBR', 0.5, -0.55], ['wheelBL', -0.5, -0.55], ['wheelFR', 0.5, 0.4], ['wheelFL', -0.5, 0.4]] as const;
  const wheelJ: Record<string, THREE.Object3D> = {};
  for (const [n, x, z] of WHEELS) {
    const j = joint(body, V(x, 0.3, z), n);
    boneWheel(j.abs, x, 0.3, z, 0.27);
    wheelJ[n] = j.p;
  }
  // Rib arches over the ram, a spine along their crown.
  for (const z of [-0.6, -0.1, 0.4]) {
    const arch = M(new THREE.TorusGeometry(0.38, 0.032, 5, 12, Math.PI), BONE, fr);
    arch.position.set(0, 0.36, z);
  }
  for (let i = 0; i < 12; i++) {
    const v = M(new THREE.CylinderGeometry(0.045, 0.05, 0.07, 7), BONE, fr);
    v.rotation.x = Math.PI / 2;
    v.position.set(0, 0.76, -0.66 + i * 0.1);
    const sp = M(new THREE.ConeGeometry(0.02, 0.1, 4), BONE, fr);
    sp.position.set(0, 0.82, -0.66 + i * 0.1);
    sp.rotation.x = -0.4;
  }
  // A torn hide stretched over the back arches like a siege tortoise.
  const hg = new THREE.CylinderGeometry(0.41, 0.41, 0.85, 12, 3, true, -Math.PI / 2, Math.PI);
  const hp = hg.attributes.position as THREE.BufferAttribute;
  for (let i = 0; i < hp.count; i++) if (Math.abs(hp.getX(i)) > 0.38) hp.setZ(i, -(rng() * 0.12 + (i % 2 ? 0.08 : 0)));
  hg.computeVertexNormals();
  const hd = M(hg, hide, fr);
  hd.rotation.x = -Math.PI / 2;
  hd.position.set(0, 0.37, -0.32);
  // Ragged flaps hanging off the hide's edges, on a pivot per side.
  const flapJ = [-1, 1].map((k) => joint(fr, V(k * 0.41, 0.4, -0.35), k < 0 ? 'flapL' : 'flapR'));
  for (let i = 0; i < 6; i++) {
    const k = i % 2 ? 1 : -1;
    const f = M(new THREE.PlaneGeometry(0.12, 0.22 + rng() * 0.1), hide, flapJ[k < 0 ? 0 : 1]!.abs);
    f.position.set(k * 0.41, 0.28, -0.65 + Math.floor(i / 2) * 0.3);
    f.rotation.y = Math.PI / 2;
    f.rotation.x = R() * 0.3;
  }
  // The ram itself: a femur on chains, swinging under the arches.
  const ramJ = joint(fr, V(0, 0.5, 0), 'ram');
  const ramG = G(ramJ.abs, 0, 0.5, 0);
  limb(ramG, V(0, 0, -0.85), V(0, 0, 0.62), 0.075, 0.09, old, 9);
  for (const x of [-0.06, 0.06]) M(new THREE.SphereGeometry(0.09, 8, 6), old, ramG).position.set(x, 0, -0.88);
  for (const z of [-0.3, 0.25, 0.55]) {
    const band = M(new THREE.TorusGeometry(0.095, 0.02, 5, 12), DSTEEL, ramG);
    band.position.z = z;
  }
  const chainJ: THREE.Object3D[] = [];
  for (const z of [-0.35, 0.25]) {
    const j = joint(fr, V(0, 0.76, z), z < 0 ? 'chainB' : 'chainF');
    chainJ.push(j.p);
    const ch = G(j.abs, 0, 0.5, z);
    limb(ch, V(0, 0, 0), V(0, 0.26, 0), 0.012, 0.012, DSTEEL, 4);
    for (let k = 0; k < 4; k++) {
      const lk = M(new THREE.TorusGeometry(0.022, 0.007, 4, 8), DSTEEL, ch);
      lk.position.set(0, 0.04 + k * 0.06, 0);
      lk.rotation.y = k * 1.57;
    }
  }
  // Skull head: a titan's, horned, eyes burning; the jaw hinged under it.
  const sk = G(ramG, 0, 0.02, 0.82);
  sk.scale.setScalar(1.25);
  const jawJ = joint(sk, V(0, -0.15, 0.06), 'jaw');
  const cr = M(new THREE.SphereGeometry(0.2, 12, 9), BONE, sk);
  cr.scale.set(1, 0.85, 1.1);
  const brow = M(new THREE.BoxGeometry(0.3, 0.06, 0.08), BONE, sk);
  brow.position.set(0, 0.1, 0.15);
  const snout = M(new THREE.CylinderGeometry(0.09, 0.13, 0.24, 8), BONE, sk);
  snout.rotation.x = Math.PI / 2;
  snout.position.set(0, -0.06, 0.24);
  const jaw = M(new THREE.BoxGeometry(0.18, 0.045, 0.26), BONE, jawJ.abs);
  jaw.position.set(0, -0.17, 0.17);
  for (let i = 0; i < 6; i++) {
    const tooth = M(new THREE.ConeGeometry(0.014, 0.06, 4), BONE, sk);
    tooth.position.set(-0.075 + i * 0.03, -0.13, 0.33);
    tooth.rotation.x = Math.PI;
  }
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.055, 7, 6), BLACK, sk).position.set(k * 0.085, 0.04, 0.16);
    // Thick horns swept forward: the battering face.
    const base = M(new THREE.SphereGeometry(0.07, 7, 6), old, sk);
    base.position.set(k * 0.17, 0.08, 0.02);
    const h1 = G(sk, k * 0.2, 0.09, 0.02);
    h1.rotation.set(1.2, 0, -k * 0.9);
    const c1 = M(new THREE.CylinderGeometry(0.045, 0.065, 0.2, 7), old, h1);
    c1.position.y = 0.09;
    const h2 = G(h1, 0, 0.18, 0);
    h2.rotation.z = k * 1.1;
    const c2 = M(new THREE.ConeGeometry(0.045, 0.26, 7), old, h2);
    c2.position.y = 0.12;
  }
  eyesGlow(sk, '#ff5a2a', 0.04, 0.2, 0.085, 0.026);
  glowSprite(sk, '#ff4a1a', 0.5, V(0, 0, 0.12), 0.35);
  // Nightmare smoke leaking from under the hide and out of the skull.
  const ribs: Ribbon[] = [];
  const wm = basic('#2b2244', 0.42);
  for (let i = 0; i < 4; i++) {
    const a = i * 1.6;
    ribs.push(new Ribbon(fr, wisp(Math.cos(a) * 0.3, -0.3 + Math.sin(a) * 0.4, 0.7, 6).map((p) => p.add(V(0, 0.5, 0))), 0.03, wm, rng, 0.05));
  }
  glowSprite(fr, '#8b4dff', 1.1, V(0, 0.4, -0.1), 0.22);
  // Only the smoke boils here: the swing, the wheels and the rocking are the clips'.
  const t = (): void => {
    for (const r of ribs) r.step();
  };
  t();
  return done(c, 0.95 * S, t, undefined, bonesOf(c, {
    frame: frameJ.p, wheelFL: wheelJ.wheelFL!, wheelFR: wheelJ.wheelFR!, wheelBL: wheelJ.wheelBL!, wheelBR: wheelJ.wheelBR!,
    chainF: chainJ[1]!, chainB: chainJ[0]!, ram: ramJ.p, jaw: jawJ.p, flapL: flapJ[0]!.p, flapR: flapJ[1]!.p,
  }));
};

/* ---------- mangonel (Mangonel de hueso) ---------- */

/**
 * A bone mangonel: a low carriage of ribs on bone wheels, a twisted skein of sinew between two uprights
 * driving a long throwing arm (a titan's spine) with a skull cup at its end, a stone of ink waiting in it,
 * and a horned skull on the prow. It throws far, over the heroes' heads, at the walls.
 * Bones: the frame rocks on four free wheels; the arm pivots on the skein (rest: lying back, cup low;
 * 1.85 rad up: struck against the stop beam); the stone sits in the cup (the clips hide it after a throw).
 */
export const mangonel = (o?: BaseOpts): Model => {
  const c = begin(o, 0.9, 41);
  const { g, R, rng } = c;
  const S = 1.25;
  const body = G(g);
  body.scale.setScalar(S);
  const old = std('#a99d84', { roughness: 0.9, bumpScale: 0.04 });
  const sinew = std('#4a1c1a', { roughness: 0.8, bumpScale: 0.05 });
  const frameJ = joint(body, V(0, 0.3, 0), 'frame');
  const fr = frameJ.abs;
  // Carriage: two long bones as rails, crossed by shorter ones, and a vertebra deck.
  for (const k of [-1, 1]) {
    limb(fr, V(k * 0.34, 0.34, -0.8), V(k * 0.34, 0.34, 0.62), 0.05, 0.045, old, 7);
    for (const z of [-0.8, 0.62]) M(new THREE.SphereGeometry(0.07, 7, 6), old, fr).position.set(k * 0.34, 0.34, z);
  }
  for (const z of [-0.6, -0.1, 0.42]) limb(fr, V(-0.46, 0.32, z), V(0.46, 0.32, z), 0.035, 0.035, old, 6);
  const WHEELS = [['wheelBR', 0.48, -0.6], ['wheelBL', -0.48, -0.6], ['wheelFR', 0.48, 0.42], ['wheelFL', -0.48, 0.42]] as const;
  const wheelJ: Record<string, THREE.Object3D> = {};
  for (const [n, x, z] of WHEELS) {
    const j = joint(body, V(x, 0.3, z), n);
    boneWheel(j.abs, x, 0.3, z, 0.25);
    wheelJ[n] = j.p;
  }
  // Two uprights of thigh bone braced fore and aft, the stop beam across their tops (the arm strikes it).
  for (const k of [-1, 1]) {
    limb(fr, V(k * 0.3, 0.34, -0.12), V(k * 0.24, 1.0, 0.02), 0.045, 0.04, BONE, 7);
    limb(fr, V(k * 0.32, 0.34, 0.4), V(k * 0.25, 0.92, 0.04), 0.03, 0.028, old, 6);
    M(new THREE.SphereGeometry(0.06, 7, 6), BONE, fr).position.set(k * 0.24, 1.0, 0.02);
  }
  limb(fr, V(-0.3, 1.0, 0.04), V(0.3, 1.0, 0.04), 0.045, 0.045, old, 7);
  // A rag of hide padding the stop beam.
  const pad = M(new THREE.CylinderGeometry(0.07, 0.07, 0.22, 8), sinew, fr);
  pad.rotation.z = Math.PI / 2;
  pad.position.set(0, 1.0, 0.04);
  // The skein: twisted sinew wound between the uprights, its turns standing proud.
  const skein = M(new THREE.CylinderGeometry(0.1, 0.1, 0.52, 10), sinew, fr);
  skein.rotation.z = Math.PI / 2;
  skein.position.set(0, 0.52, -0.12);
  for (let i = 0; i < 7; i++) {
    const turn = M(new THREE.TorusGeometry(0.105, 0.016, 4, 10), sinew, fr);
    turn.rotation.y = Math.PI / 2;
    turn.rotation.x = 0.35;
    turn.position.set(-0.22 + i * 0.073, 0.52, -0.12);
  }
  for (const k of [-1, 1]) {
    const nave = M(new THREE.CylinderGeometry(0.12, 0.12, 0.05, 8), DSTEEL, fr);
    nave.rotation.z = Math.PI / 2;
    nave.position.set(k * 0.28, 0.52, -0.12);
  }
  // The arm: a spine lying back from the skein to a skull cup; the stone of ink sits in it.
  const armJ = joint(fr, V(0, 0.52, -0.12), 'arm');
  const arm = armJ.abs;
  const cup = V(0, 0.3, -0.92);
  limb(arm, V(0, 0.54, -0.04), cup, 0.055, 0.04, old, 9);
  for (let i = 1; i < 8; i++) {
    const q = V(0, 0.54, -0.04).lerp(cup, i / 8);
    const v = M(new THREE.CylinderGeometry(0.06, 0.065, 0.045, 7), BONE, arm);
    v.position.copy(q);
    v.rotation.x = 1.3;
    const sp = M(new THREE.ConeGeometry(0.015, 0.08, 4), BONE, arm);
    sp.position.copy(q).add(V(0, 0.05, 0.015));
  }
  const bowl = M(new THREE.SphereGeometry(0.13, 10, 7, 0, Math.PI * 2, Math.PI * 0.35, Math.PI * 0.65), BONE, arm);
  bowl.position.copy(cup).add(V(0, 0.02, 0));
  for (const k of [-1, 1]) M(new THREE.SphereGeometry(0.03, 6, 5), BLACK, arm).position.copy(cup).add(V(k * 0.05, -0.02, -0.11));
  const stoneJ = joint(arm, cup.clone().add(V(0, 0.08, 0)), 'stone');
  const rock = M(new THREE.DodecahedronGeometry(0.1, 0), std('#2a2630', { roughness: 0.95, bumpScale: 0.05 }), stoneJ.abs);
  rock.position.copy(cup).add(V(0, 0.08, 0));
  rock.rotation.set(R() * 3, R() * 3, 0);
  glowSprite(stoneJ.abs, '#8b4dff', 0.45, cup.clone().add(V(0, 0.1, 0)), 0.3);
  // A horned skull on the prow, eyes burning.
  const sk = G(fr, 0, 0.42, 0.72);
  const cr = M(new THREE.SphereGeometry(0.16, 12, 9), BONE, sk);
  cr.scale.set(1, 0.85, 1.1);
  const snout = M(new THREE.CylinderGeometry(0.07, 0.1, 0.18, 8), BONE, sk);
  snout.rotation.x = Math.PI / 2;
  snout.position.set(0, -0.05, 0.18);
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.045, 7, 6), BLACK, sk).position.set(k * 0.07, 0.03, 0.13);
    const h1 = G(sk, k * 0.14, 0.08, 0);
    h1.rotation.set(-0.5, 0, -k * 1.0);
    M(new THREE.ConeGeometry(0.04, 0.22, 7), old, h1).position.y = 0.1;
  }
  eyesGlow(sk, '#ff5a2a', 0.035, 0.16, 0.07, 0.022);
  // Nightmare smoke rising off the skein.
  const ribs: Ribbon[] = [];
  const wm = basic('#2b2244', 0.42);
  for (let i = 0; i < 3; i++) {
    const a = i * 2.1;
    ribs.push(new Ribbon(fr, wisp(Math.cos(a) * 0.25, Math.sin(a) * 0.3, 0.6, 6).map((p) => p.add(V(0, 0.6, -0.12))), 0.03, wm, rng, 0.05));
  }
  glowSprite(fr, '#8b4dff', 1.0, V(0, 0.55, -0.12), 0.2);
  const t = (): void => {
    for (const r of ribs) r.step();
  };
  t();
  return done(c, 1.05 * S, t, undefined, bonesOf(c, {
    frame: frameJ.p, wheelFL: wheelJ.wheelFL!, wheelFR: wheelJ.wheelFR!, wheelBL: wheelJ.wheelBL!, wheelBR: wheelJ.wheelBR!,
    arm: armJ.p, stone: stoneJ.p,
  }));
};

/* ---------- archer (Arquero espectral) ---------- */

/** Where the archer's string, arrow and drawing arm sit at rest (the clips move them; see anim/creatures/siege.ts). */
export const ARCHER = {
  /** Bow tips and the nock at rest (a light draw), in the chest bone's frame (model coordinates at rest). */
  top: V(-0.06, 1.995, 0.395),
  bot: V(-0.06, 0.845, 0.395),
  nock: V(-0.06, 1.42, 0.34),
  shoulder: V(0.17, 1.42, 0.0),
  elbow: V(0.26, 1.44, 0.16),
  hand: V(0.0, 1.42, 0.32),
};

/**
 * A pale, walking ghost of a bowman: hooded, tattered, an arrow of cold light always on the string.
 * Bones: body at the hips (the robe drifts on its own pivot), chest above it carrying the bow arm and bow,
 * the head, the mantle, the drawing arm (shoulder and elbow), the arrow and the two halves of the string
 * (each pivots at its bow tip and stretches to the nock); the legs bend at the knee.
 */
export const archer = (o?: BaseOpts): Model => {
  const c = begin(o, 0.62, 40);
  const { g, R, rng } = c;
  // Faint self-light kept under the seasonal reglow threshold (0.2), so a holiday recolors only the arrows and eyes.
  const ghost = smoke('#a9bccd', 0.6, { emissive: '#7da4c4', emissiveIntensity: 0.15, side: THREE.DoubleSide });
  const wood = std('#5d646c', { roughness: 0.8 });
  const light = emis('#e6f8ff', '#8fd8ff', 1.8);
  const A = ARCHER;
  const bodyJ = joint(g, V(0, 0.9, 0), 'body');
  const robeJ = joint(bodyJ.abs, V(0, 1.3, 0), 'robe');
  const chestJ = joint(bodyJ.abs, V(0, 1.15, 0), 'chest');
  const ch = chestJ.abs;
  const headJ = joint(ch, V(0, 1.52, 0.03), 'head');
  const mantleJ = joint(ch, V(0, 1.52, 0), 'mantle');
  // Legs that walk (unlike the wraith), fading into the hem.
  const legJ = [-1, 1].map((k) => joint(g, V(k * 0.1, 0.82, 0), k < 0 ? 'legL' : 'legR'));
  const shinJ = [-1, 1].map((k, i) => joint(legJ[i]!.abs, V(k * 0.1, 0.42, 0.04), k < 0 ? 'shinL' : 'shinR'));
  for (const k of [-1, 1]) {
    const i = k < 0 ? 0 : 1;
    const x = k * 0.1;
    limb(legJ[i]!.abs, V(x, 0.82, 0), V(x, 0.42, 0.04), 0.06, 0.045, ghost, 6);
    limb(shinJ[i]!.abs, V(x, 0.42, 0.04), V(x, 0.04, -0.02), 0.045, 0.03, ghost, 6);
    M(new THREE.BoxGeometry(0.08, 0.04, 0.16), ghost, shinJ[i]!.abs).position.set(x, 0.02, 0.03);
  }
  // Ragged robe to the knee.
  const rg = new THREE.ConeGeometry(0.32, 0.95, 14, 4, true);
  const rp = rg.attributes.position as THREE.BufferAttribute;
  for (let i = 0; i < rp.count; i++) if (rp.getY(i) < -0.4) rp.setY(i, rp.getY(i) + (i % 2 ? 0.18 : rng() * 0.1));
  rg.computeVertexNormals();
  M(rg, ghost, robeJ.abs).position.y = 0.88;
  // Chest and shoulders.
  const chest = M(new THREE.SphereGeometry(0.17, 10, 8), ghost, ch);
  chest.scale.set(1.15, 1.25, 0.8);
  chest.position.set(0, 1.32, 0);
  // Deep hood, a void inside with two cold eyes.
  const head = G(headJ.abs, 0, 1.62, 0.03);
  head.rotation.x = 0.15;
  const hood = M(new THREE.SphereGeometry(0.15, 12, 8, Math.PI / 2 + 0.85, Math.PI * 2 - 1.7, 0, Math.PI / 1.35), ghost, head);
  hood.rotation.x = -0.2;
  const tip = M(new THREE.ConeGeometry(0.09, 0.22, 8, 1, true), ghost, head);
  tip.position.set(0, 0.08, -0.14);
  tip.rotation.x = -2.0;
  M(new THREE.SphereGeometry(0.1, 8, 6), BLACK, head).position.set(0, -0.01, -0.005);
  eyesGlow(head, '#bfeaff', 0, 0.12, 0.04, 0.018);
  // Short mantle around the shoulders.
  const mg = new THREE.ConeGeometry(0.27, 0.24, 12, 2, true);
  const mp = mg.attributes.position as THREE.BufferAttribute;
  for (let i = 0; i < mp.count; i++) if (mp.getY(i) < -0.05) mp.setY(i, mp.getY(i) + (i % 2 ? 0.07 : 0));
  mg.computeVertexNormals();
  M(mg, ghost, mantleJ.abs).position.y = 1.44;
  // Quiver on the back, arrows fletched with cold light.
  const qv = G(ch, 0.1, 1.28, -0.16);
  qv.rotation.set(-0.25, 0, -0.35);
  M(new THREE.CylinderGeometry(0.055, 0.045, 0.42, 8), DWOOD, qv);
  ring(qv, 0.057, 0.008, DSTEEL, 0.19, 4, 10);
  for (let i = 0; i < 4; i++) {
    const x = (i % 2 ? 0.02 : -0.02) + R() * 0.01, z = i < 2 ? 0.018 : -0.018;
    limb(qv, V(x, 0.18, z), V(x, 0.33, z), 0.006, 0.006, wood, 4);
    M(new THREE.ConeGeometry(0.018, 0.06, 3), light, qv).position.set(x, 0.33, z);
  }
  // Bow arm out in front, the longbow upright; the drawing hand pulls to the cheek.
  const shL = V(-0.17, 1.42, 0.02), handL = V(-0.06, 1.42, 0.5);
  limb(ch, shL, V(-0.13, 1.42, 0.27), 0.045, 0.04, ghost, 6);
  limb(ch, V(-0.13, 1.42, 0.27), handL, 0.04, 0.032, ghost, 6);
  const bow: THREE.Vector3[] = [];
  for (let j = 0; j <= 8; j++) {
    const s = j / 8 - 0.5;
    bow.push(V(-0.06, 1.42 + s * 1.15, 0.52 - s * s * 0.5));
  }
  M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(bow), 16, 0.016, 5), wood, ch);
  M(new THREE.CylinderGeometry(0.024, 0.024, 0.12, 6), DWOOD, ch).position.copy(handL).setX(-0.06);
  // The string in two halves, each from its bow tip to the nock (the clips swing and stretch them).
  const unit = new THREE.CylinderGeometry(0.004, 0.004, 1, 4);
  const strT = joint(ch, A.top, 'strT'), strB = joint(ch, A.bot, 'strB');
  aim(M(unit, light, strT.abs), A.top, A.nock);
  aim(M(unit, light, strB.abs), A.bot, A.nock);
  // The drawing arm: upper arm from the shoulder, forearm from the elbow to the hand at the nock.
  const armJ = joint(ch, A.shoulder, 'armR');
  const foreJ = joint(armJ.abs, A.elbow, 'foreR');
  aim(M(new THREE.CylinderGeometry(0.045, 0.035, 1, 6), ghost, armJ.abs), A.shoulder, A.elbow);
  aim(M(new THREE.CylinderGeometry(0.035, 0.03, 1, 6), ghost, foreJ.abs), A.elbow, A.hand);
  const arrowJ = joint(ch, A.nock, 'arrow');
  const arrow = G(arrowJ.abs, A.nock.x, A.nock.y, A.nock.z);
  limb(arrow, V(0, 0, 0), V(0, 0, 0.62), 0.006, 0.006, light, 4);
  const head2 = M(new THREE.ConeGeometry(0.02, 0.07, 4), light, arrow);
  head2.position.z = 0.65;
  head2.rotation.x = Math.PI / 2;
  glowSprite(arrow, '#9fdcff', 0.22, V(0, 0, 0.64), 0.85);
  // Cold wisps trailing from the robe and the shoulders.
  const ribs: Ribbon[] = [];
  const wm = basic('#c4dcef', 0.28);
  for (let i = 0; i < 4; i++) {
    const a = i * 1.57 + 0.4;
    ribs.push(new Ribbon(robeJ.abs, wisp(Math.cos(a) * 0.22, Math.sin(a) * 0.2 - 0.05, 0.9, 7).map((p) => p.add(V(0, 0.45, 0))), 0.022, wm, rng, 0.05));
  }
  glowSprite(ch, '#8fc8ff', 0.9, V(0, 1.2, 0), 0.18);
  // Only the wisps boil here: the draw and the stride are the clips'.
  const t = (): void => {
    for (const r of ribs) r.step();
  };
  t();
  return done(c, 1.85, t, undefined, bonesOf(c, {
    body: bodyJ.p, robe: robeJ.p, chest: chestJ.p, head: headJ.p, mantle: mantleJ.p, armR: armJ.p, foreR: foreJ.p,
    arrow: arrowJ.p, strT: strT.p, strB: strB.p, legL: legJ[0]!.p, shinL: shinJ[0]!.p, legR: legJ[1]!.p, shinR: shinJ[1]!.p,
  }));
};
